/**
 * Подробная аналитика по кампаниям: одна большая таблица на всю ширину
 * (перетаскивание мышью, липкие шапка/первый столбец) + сохранённые фильтры
 * отдельными таблицами + срезы аудитории + чистка трафика.
 */
import { useState } from "react";
import type { CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { fetchMarketingAccounts, fetchMarketingCampaigns, fetchMarketingCampaignFunnel, fetchMarketingOverview } from "@/api/dashboard";
import { CampaignsTable, BreakdownPanel, CleanupPanel, CampaignScatter, CampaignDonut } from "@/pages/MarketingPage";
import type { CampFilters } from "@/pages/MarketingPage";
import type { CampaignFunnel } from "@/api/dashboard";

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

type Preset = { id: string; name: string; filters: CampFilters };
const PRESETS_KEY = "mkt_analytics_presets";
function loadPresets(): Preset[] {
  try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || "[]"); } catch { return []; }
}

export function MarketingAnalyticsPage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const [presets, setPresets] = useState<Preset[]>(loadPresets);
  const [hideViz, setHideViz] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem("mkt_analytics_hideviz") || "[]")); } catch { return new Set(); } });
  const toggleViz = (k: string) => setHideViz(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); try { localStorage.setItem("mkt_analytics_hideviz", JSON.stringify([...n])); } catch { /* ignore */ } return n; });

  if (!hasAccess(user, "marketing")) {
    return <div style={{ padding: 40, textAlign: "center", color: "var(--mute)" }}>Раздел доступен только отделу маркетинга.</div>;
  }

  const presetTo = isoLocal(new Date());
  const pf = new Date(); pf.setDate(pf.getDate() - (days - 1));
  const presetFrom = isoLocal(pf);
  const range = custom ? { date_from: custom.from, date_to: custom.to } : { date_from: presetFrom, date_to: presetTo };

  // Фильтр по кабинету (владелец 17.07): серверная фильтрация /campaigns?account_id=
  const [accountId, setAccountId] = useState<number | undefined>(() => {
    try { const v = localStorage.getItem("mkt_analytics_account"); return v ? Number(v) : undefined; } catch { return undefined; }
  });
  const pickAccount = (v: string) => {
    const id = v ? Number(v) : undefined;
    setAccountId(id);
    try { id ? localStorage.setItem("mkt_analytics_account", String(id)) : localStorage.removeItem("mkt_analytics_account"); } catch { /* ignore */ }
  };
  const accountsQ = useQuery({ queryKey: ["marketing/accounts"], queryFn: fetchMarketingAccounts, staleTime: 300_000 });
  const campaignsQ = useQuery({
    queryKey: ["marketing/campaigns", range.date_from, range.date_to, accountId ?? 0],
    queryFn: () => fetchMarketingCampaigns({ ...range, account_id: accountId }),
  });
  const cfQ = useQuery({
    queryKey: ["marketing/campaign-funnel", range.date_from, range.date_to],
    queryFn: () => fetchMarketingCampaignFunnel(range),
  });
  // Расход по ВСЕМ кабинетам за тот же период — чтобы было видно, что прячет фильтр.
  const allSpendQ = useQuery({
    queryKey: ["marketing/overview-all", range.date_from, range.date_to],
    queryFn: () => fetchMarketingOverview(range),
    enabled: accountId !== undefined,
    staleTime: 300_000,
  });
  const camps = campaignsQ.data?.campaigns ?? [];
  const convGoals = campaignsQ.data?.goals ?? [];
  const crm = cfQ.data?.configured ? { stages: cfQ.data.stages, byCampaign: cfQ.data.campaigns } : undefined;

  const savePresets = (next: Preset[]) => { setPresets(next); try { localStorage.setItem(PRESETS_KEY, JSON.stringify(next)); } catch { /* ignore */ } };
  const addPreset = (filters: CampFilters) => {
    const def = [filters.search, filters.status, filters.type].filter(Boolean).join(" · ") || "фильтр";
    const name = (window.prompt("Название сохранённого фильтра:", def) || "").trim();
    if (!name) return;
    savePresets([...presets, { id: `p${Date.now()}`, name, filters }]);
  };

  return (
    <div style={{ padding: "14px 14px", width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Подробная аналитика</h1>
          <div style={{ marginTop: 4, fontSize: 12.5, color: "var(--mute)" }}>
            Кампании, срезы аудитории, чистка трафика · <Link to="/" style={{ color: "var(--brick)" }}>← на дашборд</Link>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <select className="input-sm" value={custom ? "custom" : String(days)} onChange={e => {
            if (e.target.value === "custom") setCustom({ from: presetFrom, to: presetTo });
            else { setCustom(null); setDays(Number(e.target.value)); }
          }}>
            <option value="7">7 дней</option>
            <option value="14">14 дней</option>
            <option value="30">30 дней</option>
            <option value="60">60 дней</option>
            <option value="90">90 дней</option>
            <option value="custom">Свой период</option>
          </select>
          {custom && (
            <>
              <input className="input-sm" type="date" value={custom.from} max={custom.to} onChange={e => setCustom({ ...custom, from: e.target.value })}/>
              <span style={{ color: "var(--mute)", fontSize: 12 }}>—</span>
              <input className="input-sm" type="date" value={custom.to} min={custom.from} max={presetTo} onChange={e => setCustom({ ...custom, to: e.target.value })}/>
            </>
          )}
        </div>
      </div>

      {accountId !== undefined && (() => {
        const shown = camps.reduce((s, c) => s + (c.cost_rub || 0), 0);
        const all = allSpendQ.data?.totals.cost_rub ?? 0;
        if (!all || all - shown < 1) return null;
        const rub = (v: number) => `${Math.round(v).toLocaleString("ru-RU")} ₽`;
        return (
          <div style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            fontSize: 13, lineHeight: 1.5, marginBottom: 10, padding: "9px 12px",
            border: "1px solid var(--line)", borderRadius: 8, background: "var(--bg-soft, transparent)",
          }}>
            <span>
              Показан только кабинет <b>{(accountsQ.data ?? []).find(a => a.id === accountId)?.title
                || (accountsQ.data ?? []).find(a => a.id === accountId)?.login || accountId}</b>:
              {" "}<b>{rub(shown)}</b> расхода из <b>{rub(all)}</b> по всем кабинетам за период.
              {" "}Остальные <b>{rub(all - shown)}</b> в таблицу не попали.
            </span>
            <button className="btn btn-sm" onClick={() => pickAccount("")}>Показать все кабинеты</button>
          </div>
        );
      })()}

      <CampaignsTable campaigns={camps} loading={campaignsQ.isLoading} storageKey="mkt_cols_analytics"
        title="Кампании — все метрики" crm={crm} convGoals={convGoals} onSavePreset={addPreset}
        headerExtra={
          <select className="input-sm" value={accountId ?? ""} onChange={e => pickAccount(e.target.value)} title="Кабинет Я.Директа">
            <option value="">Все кабинеты</option>
            {(accountsQ.data ?? []).map(a => <option key={a.id} value={a.id}>{a.title || a.login}</option>)}
          </select>
        }/>
      {cfQ.data && !cfQ.data.configured && cfQ.data.error && (
        <div style={{ fontSize: 12, color: "var(--mute)", marginTop: -8, marginBottom: 8 }}>
          CRM по кампаниям: {cfQ.data.error}
        </div>
      )}

      {/* Сделки без utm_campaign не попадают НИ В ОДНУ строку — без этой сноски
          «Итого» по CRM-столбцам выглядит меньше, чем реальная воронка в Amo. */}
      {(cfQ.data?.unattributed?.total ?? 0) > 0 && (() => {
        const u = cfQ.data!.unattributed!;
        const deep = (cfQ.data!.stages ?? [])
          .map(s => ({ name: s.name, n: u.stages?.[String(s.stage_id)] ?? 0 }))
          .filter(x => x.n > 0);
        return (
          <div style={{
            fontSize: 12.5, lineHeight: 1.55, color: "var(--mute)", marginTop: -6, marginBottom: 12,
            padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8, maxWidth: 980,
          }}>
            Вне таблицы: <b style={{ color: "var(--fg)" }}>{u.total}</b> сделок за период без привязки к кампании
            {" "}(нет utm_campaign — {u.empty}{u.foreign ? `, значение не ID кампании — ${u.foreign}` : ""}).
            {deep.length > 0 && <> Из них дошли до этапов: {deep.map(x => `${x.name} — ${x.n}`).join(", ")}.</>}
            {" "}Такие сделки не попадают ни в одну строку, поэтому «Итого» по CRM-столбцам меньше, чем воронка в Amo.
          </div>
        );
      })()}

      {/* Карта кампаний — скаттер-квадранты (расход × ROMI/CPA, размер = конверсии) */}
      {!hideViz.has("scatter") && <CampaignScatter campaigns={camps} crm={crm} onHide={() => toggleViz("scatter")}/>}
      {!hideViz.has("donut") && <CampaignDonut campaigns={camps} crm={crm} onHide={() => toggleViz("donut")}/>}
      {hideViz.size > 0 && (
        <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => { setHideViz(new Set()); try { localStorage.removeItem("mkt_analytics_hideviz"); } catch { /* ignore */ } }}>
          Показать скрытые ({hideViz.size})
        </button>
      )}

      {/* Сохранённые фильтры — отдельными таблицами под основной */}
      {presets.map(p => (
        <CampaignsTable key={p.id} campaigns={camps} loading={campaignsQ.isLoading} storageKey={`mkt_cols_preset_${p.id}`}
          title={`Фильтр: ${p.name}`} crm={crm} convGoals={convGoals} initialFilters={p.filters} compact
          presetActions={<button className="btn btn-sm" title="Удалить сохранённый фильтр"
                                  onClick={() => savePresets(presets.filter(x => x.id !== p.id))}>✕ Удалить</button>}/>
      ))}

      <BreakdownPanel range={range}/>
      <CleanupPanel range={range}/>
      <UnattributedContracts data={cfQ.data}/>
    </div>
  );
}

/** Договоры, которые не привязаны ни к одной кампании (нет utm_campaign).
 *  В таблице кампаний их не видно вообще — а это самые дорогие сделки. */
function UnattributedContracts({ data }: { data?: CampaignFunnel }) {
  const u = data?.unattributed;
  const deals = u?.deals ?? [];
  if (!deals.length) return null;
  const total = deals.reduce((s, d) => s + (d.price || 0), 0);
  const fmtD = (ts?: number | null) =>
    ts ? new Date(ts * 1000).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
  const fmtM = (v: number) => v ? `${Math.round(v).toLocaleString("ru-RU")} ₽` : "—";
  const th: CSSProperties = {
    textAlign: "left", padding: "7px 10px", fontSize: 11, letterSpacing: ".04em",
    textTransform: "uppercase", color: "var(--mute)", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap",
  };
  const td: CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--line)", verticalAlign: "top" };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h3 className="card-title" style={{ margin: 0 }}>Договоры без рекламной метки · {deals.length}</h3>
        <div style={{ fontSize: 13, color: "var(--mute)" }}>
          на сумму <b style={{ color: "var(--fg)" }}>{fmtM(total)}</b>
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--mute)", marginTop: 6, lineHeight: 1.5 }}>
        У этих сделок не заполнен <code>utm_campaign</code>, поэтому они не попадают ни в одну строку таблицы
        кампаний и не учитываются в стоимости договора. Дата — вход в договорный этап за выбранный период.
      </div>
      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>Договор</th>
              <th style={th}>Сделка</th>
              <th style={{ ...th, textAlign: "right" }}>Сумма</th>
              <th style={th}>Создана</th>
              <th style={th}>Этап сейчас</th>
              <th style={th}>Менеджер</th>
              <th style={th}>Метка</th>
            </tr>
          </thead>
          <tbody>
            {deals.map(d => (
              <tr key={d.id}>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtD(d.contract_at)}</td>
                <td style={td}>
                  {d.amo_url
                    ? <a href={d.amo_url} target="_blank" rel="noreferrer" style={{ color: "var(--brick)" }}>{d.name}</a>
                    : d.name}
                  <div style={{ fontSize: 11, color: "var(--mute)" }}>#{d.id} · {d.pipeline_name || "—"}</div>
                </td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  {fmtM(d.price)}
                </td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtD(d.created_at)}</td>
                <td style={td}>{d.status_name || "—"}</td>
                <td style={td}>{d.manager || "—"}</td>
                <td style={td}>{d.utm_campaign ? <code>{d.utm_campaign}</code> : "нет"}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...td, fontWeight: 700 }}>Итого</td>
              <td style={td}/>
              <td style={{ ...td, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtM(total)}</td>
              <td style={td} colSpan={4}/>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MarketingAnalyticsPage;
